import { BulkCapabilityResponse } from 'types/capabilities/capabilities'
import { EnableFiatCurrencyFilter } from 'types/types'

export interface WalletProperties {
  currency: string
  supportsWithdrawals: boolean
  supportsThirdParty: boolean
  supportsDeposits: boolean
  lsid: string
  isLegacyWallet: boolean
}

interface FiatWalletCapabilities {
  isDepositEnabled?: boolean
  isWithdrawEnabled?: boolean
  isPayEnabled?: boolean
  isTransferEnabled?: boolean
}

interface FiatWalletActions {
  supportsPay: boolean
  supportsDeposits: boolean
  supportsWithdrawals: boolean
  supportsInternalTransfers: boolean
}

interface GetFiatWalletActionSupportProps {
  walletProperties: WalletProperties
  bulkCapabilities: BulkCapabilityResponse
  enableFiatCurrencyFilter: EnableFiatCurrencyFilter
}

const walletActionsSupport = (
  walletProperties: WalletProperties,
  enableFiatCurrencyFilter: EnableFiatCurrencyFilter
) => {
  const { currency, supportsWithdrawals, supportsThirdParty, supportsDeposits } = walletProperties
  const supportedWithdrawCurrencies = enableFiatCurrencyFilter?.supportedWithdrawCurrencies || []
  const supportedDepositCurrencies = enableFiatCurrencyFilter?.supportedDepositCurrencies || []
  const supportedPayCurrencies = enableFiatCurrencyFilter?.supportedPayCurrencies || []
  const walletSupportsWithdrawals =
    supportsWithdrawals && supportedWithdrawCurrencies.includes(currency?.toUpperCase())
  const walletSupportsPay =
    supportsThirdParty && supportedPayCurrencies?.includes(currency?.toUpperCase())
  const walletSupportsDeposits =
    (supportedDepositCurrencies?.includes(currency?.toUpperCase()) && supportsDeposits) ||
    (!supportedDepositCurrencies && supportsDeposits)

  return {
    supportsPay: walletSupportsPay,
    supportsDeposits: walletSupportsDeposits,
    supportsWithdrawals: walletSupportsWithdrawals,
  }
}

/**
 * Returns wallet actions capability support for a wallet
 *
 * @param {WalletProperties} walletProperties - Some basic wallet properties used to determine wallet action capabilities
 * @param {BulkCapabilityResponse} bulkCapabilities - Capabilities from the capabilities management service used to determine wallet action capabilities
 * @returns {FiatWalletActions} - Returns the actions (deposit, pay, withdraw and internal transfers),
 * that a given wallet is enabled for.
 *
 * - If No bulk capabilities are recieved, defalt to existing logic.
 *   This logic will be removed once all processing profile capabilities are created
 *   processing profile capabilities are created
 */
export const getFiatWalletActionSupport = ({
  walletProperties,
  bulkCapabilities,
  enableFiatCurrencyFilter,
}: GetFiatWalletActionSupportProps): FiatWalletActions => {
  if (bulkCapabilities) {
    const { isDepositEnabled, isPayEnabled, isWithdrawEnabled, isTransferEnabled } =
      getFiatWalletCapabilities(walletProperties?.lsid, bulkCapabilities)

    const isActionCapabilityDefined =
      isDepositEnabled !== undefined &&
      isPayEnabled !== undefined &&
      isWithdrawEnabled !== undefined
    const isTransferActionCapabilityDefined = isTransferEnabled !== undefined

    if (walletProperties?.isLegacyWallet) {
      const { supportsPay, supportsDeposits, supportsWithdrawals } = walletActionsSupport(
        walletProperties,
        enableFiatCurrencyFilter
      )

      return {
        supportsPay,
        supportsDeposits,
        supportsWithdrawals,
        supportsInternalTransfers: isTransferActionCapabilityDefined && isTransferEnabled,
      }
    }

    return {
      supportsPay: isActionCapabilityDefined && isPayEnabled,
      supportsDeposits: isActionCapabilityDefined && isDepositEnabled,
      supportsWithdrawals: isActionCapabilityDefined && isWithdrawEnabled,
      supportsInternalTransfers: isTransferActionCapabilityDefined && isTransferEnabled,
    }
  }

  return {
    supportsPay: false,
    supportsDeposits: false,
    supportsWithdrawals: false,
    supportsInternalTransfers: false,
  }
}

/**
 * Returns wallet action capabilities from the capabilities management service
 *
 * @param {string} walletLsid - The wallet to fetch wallet action capabilitys for
 * @param {BulkCapabilityResponse} bulkCapabilities - The bulk wallet capabilities response.
 * @returns {FiatWalletCapabilities} - Returns the actions (deposit, pay, withdraw and internal transfers),
 * that a given wallet is enabled for.
 */
export const getFiatWalletCapabilities = (
  walletLsid: string,
  bulkCapabilities: BulkCapabilityResponse
): FiatWalletCapabilities => {
  const walletActionsCapabilityResponse =
    bulkCapabilities.FIAT?.WALLET_ACTIONS?.WALLET?.[walletLsid]
  const actions = walletActionsCapabilityResponse?.value?.toString()?.split(',')

  const walletInternalTransfersCapabilityResponse =
    bulkCapabilities.PAYMENT?.INTERNAL?.WALLET?.[walletLsid]
  const isTransferEnabled = walletInternalTransfersCapabilityResponse?.value === 'true'

  const actionsValue = {
    ...(walletActionsCapabilityResponse &&
      !!walletActionsCapabilityResponse?.value &&
      !walletActionsCapabilityResponse?.error && {
      isDepositEnabled: actions?.includes('deposit'),
      isPayEnabled: actions?.includes('pay'),
      isWithdrawEnabled: actions?.includes('withdraw'),
    }),
  }

  const internalTransferValue = {
    ...(walletInternalTransfersCapabilityResponse &&
      !!walletInternalTransfersCapabilityResponse?.value &&
      !walletInternalTransfersCapabilityResponse?.error && { isTransferEnabled }),
  }

  return {
    ...actionsValue,
    ...internalTransferValue,
  }
}
